/**
 * P3-007: Navigation runtime — consumes backend navigation contracts.
 *
 * Does not infer access from role names or permission-array position.
 * Uses backend decisions to determine which shell and canonical route items
 * are available for the active persona and scope.
 */
import type {
  ApiSuccessEnvelope,
  NavigationContract,
  NavigationContractItem,
  RouteAccessDecision,
} from '@finance/contracts';
import { useContext, useMemo } from 'react';
import { QueryClient, QueryClientContext } from '@tanstack/react-query';
import { useNavigationRoutesReadGet, useNavigationShellRead } from '../../api/generated/client';
import { selectApiData } from '../../api/select-api-data';
import { useSession } from '../../features/auth/session-provider';

export const NAVIGATION_QUERY_KEY = ['navigation'] as const;
export const ROUTE_DECISIONS_QUERY_KEY = ['navigation', 'routes'] as const;

function useNavigationQueryClient() {
  const clientFromContext = useContext(QueryClientContext);
  return useMemo(
    () =>
      clientFromContext ??
      new QueryClient({ defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } } }),
    [clientFromContext],
  );
}

function useAuthorizationQueryKey(prefix: readonly string[]) {
  const { currentContext, user } = useSession();
  return [
    ...prefix,
    user?.id,
    currentContext?.organizationId,
    currentContext?.branchId,
    currentContext?.membershipId,
    user?.entitlementsVersion,
  ] as const;
}

/**
 * Fetch the backend-authoritative shell navigation contract.
 * The query key includes the full active authorization context so stale
 * navigation is never reused after a persona, context, or entitlement change.
 */
export function useBackendNavigation() {
  const queryKey = useAuthorizationQueryKey(NAVIGATION_QUERY_KEY);
  const queryClient = useNavigationQueryClient();
  return useNavigationShellRead<NavigationContract>({
    query: {
      queryKey,
      retry: 1,
      staleTime: 60_000,
      select: (response: unknown) => {
        const payload = selectApiData(
          response as ApiSuccessEnvelope<NavigationContract | { navigation: NavigationContract }>,
        );
        return 'navigation' in payload ? payload.navigation : payload;
      },
    },
  }, queryClient);
}

/**
 * Fetch access decisions for every canonical authenticated route.
 * Smart search consumes this list so it can expose all authorized destinations
 * without guessing access from client-side role names or route metadata.
 */
export function useAuthorizedRouteDecisions() {
  const queryKey = useAuthorizationQueryKey(ROUTE_DECISIONS_QUERY_KEY);
  const queryClient = useNavigationQueryClient();
  return useNavigationRoutesReadGet<RouteAccessDecision[]>({
    query: {
      queryKey,
      retry: 1,
      staleTime: 60_000,
      select: (response: unknown) => selectApiData(response as ApiSuccessEnvelope<RouteAccessDecision[]>),
    },
  }, queryClient);
}

/**
 * Partition navigation items by availability.
 * AVAILABLE and READ_ONLY items are actionable; other non-hidden states are
 * retained as disabled items so callers can explain the restriction.
 */
export function filterNavigationItems(
  items: NavigationContractItem[],
): { visible: NavigationContractItem[]; disabled: NavigationContractItem[] } {
  const visible: NavigationContractItem[] = [];
  const disabled: NavigationContractItem[] = [];

  for (const item of items) {
    if (item.availability === 'AVAILABLE') {
      visible.push(item);
    } else if (item.availability !== 'HIDDEN') {
      disabled.push(item);
    }
  }

  return { visible, disabled };
}

/** Check accessibility using only the backend contract. */
export function isItemAccessible(item: NavigationContractItem): boolean {
  return item.availability === 'AVAILABLE' || item.availability === 'READ_ONLY';
}
